/* eslint-disable @typescript-eslint/no-var-requires */
import {
  getDate,
  addMinutes,
  isAfter,
  isBefore,
  parseISO,
  format,
  addMonths,
} from "date-fns";
import {
  AvailabilityDayType,
  AvailabilityType,
  CountType,
  ItemEditorFilterTypes,
  ItemTypes,
  PreselectType,
  ProofStatus,
  SettingsSubType,
} from "../ts/enums";
import {
  ITaskItem,
  IEventItem,
  IPenaltyItem,
  IPermissionItem,
  ISettingsItem,
  IUserRecord,
  ITaskTimerInfo,
  IPenaltyTimerInfo,
  PositionItem,
  TaskItem,
  PermissionItem,
  EventItem,
  PenaltyItem,
  ICategoryItem,
  ICountSupport,
  IVotableItem,
  ICachedRandomSelectionProvider,
} from "../ts/Definitions";
import Settings from "../controllers/settings";
import { SessionController } from "../controllers/SessionController";
import { Buffer } from "buffer";
import { IItemSupport } from "../ts/Definitions";
import { STATE_KEY_FAVOURITE_ITEMS } from "../controllers/StatefulSettings";
import random from "lodash/random";
import sample from "lodash/sample";
import _shuffle from "lodash/shuffle";
import { set } from "lodash";

export function isAvailableItem(item: ISettingsItem) {
  return (
    item &&
    !item.hidden &&
    !item.isNew &&
    !item.isDeleted &&
    !item.requiresValidation
  );
}

export function isBrowserSize(breakpoint: string) {
  return breakpoint !== "small" && breakpoint !== "xsmall";
}

export function isRecord(
  a: Record<string, any> | string[]
): a is Record<string, any> {
  // Check if a is an object and has a property called "key"
  return typeof a === "object";
}

export function checkImageURLFormat(url: string | undefined) {
  if (!url) return false;
  return (
    url.match(
      /^(http|https):\/\/.*\.(jpeg|jpg|gif|png|apng|avif|svg|webp|tiff|bmp|ico)$/
    ) != null
  );
}

export function cloneObject<T>(obj: T) {
  if (!obj) return undefined;
  return JSON.parse(JSON.stringify(obj)) as T;
}

// Cache failed image URLs to prevent retry storms from network noise
const failedImageCache = new Set<string>();

function isImageOk(img: HTMLImageElement) {
  // During the onload event, IE correctly identifies any images that
  // weren't downloaded as not complete. Others should too. Gecko-based
  // browsers act like NS4 in that they report this incorrectly.
  if (!img.complete) {
    return false;
  }

  // However, they do have two very useful properties: naturalWidth and
  // naturalHeight. These give the true size of the image. If it failed
  // to load, either of these should be zero.
  if (typeof img.naturalWidth !== "undefined" && img.naturalWidth == 0) {
    return false;
  }

  // No other way of checking: assume it's ok.
  return true;
}

export const checkImageExists = (
  path: string
): Promise<{ path: string; status: string; isOk: boolean }> => {
  // Return cached failure immediately to avoid network retry storm
  if (failedImageCache.has(path)) {
    return Promise.reject({ path, status: "error", isOK: false });
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (!isImageOk(img)) {
        failedImageCache.add(path);
        reject({ path, status: "error", isOK: false });
      } else {
        resolve({ path, status: "ok", isOk: true });
      }
    };
    img.onerror = () => {
      failedImageCache.add(path);
      reject({ path, status: "error", isOK: false });
    };
    img.src = path;
  });
};

const onlineMinuteLimit = -4;

export const isMemberActive = (lastUpdateTime: Date): boolean => {
  return isAfter(lastUpdateTime, addMinutes(new Date(), onlineMinuteLimit));
};

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const isTimedItem = (countType: CountType | undefined): boolean => {
  return (
    countType === CountType.minutes ||
    countType === CountType.hours ||
    countType === CountType.days
  );
};
export const getCountMinutes = (
  count: number,
  countType: CountType
): number => {
  let expectedCount = count;
  if (countType === CountType.hours) expectedCount = expectedCount * 60; // convert to minutes
  if (countType === CountType.days) expectedCount = expectedCount * 60 * 24; // convert to minutes
  return expectedCount;
};
const getCountHours = (count: number, countType: CountType): number => {
  let expectedCount = count;
  if (countType === CountType.minutes) expectedCount = expectedCount / 60; // convert to hours
  if (countType === CountType.days) expectedCount = expectedCount * 24; // convert to hours
  return expectedCount;
};

// returns a duration for an item
export const getRandomCountForItem = (
  count: number | undefined,
  variance: number,
  round: boolean
): number => {
  if (!count) return 0;
  if (round)
    return getDecimalRandom(count * (1 - variance), count * (1 + variance), 1);
  return getRandom(count * (1 - variance), count * (1 + variance));
};

export function withTimeout<T>(millis: number, promise: Promise<T>) {
  let handle: NodeJS.Timeout;
  const timeout = new Promise((_resolve, reject) => {
    handle = setTimeout(() => reject(`Timed out after ${millis} ms.`), millis);
  });
  return Promise.race([promise, timeout]).then(() => clearTimeout(handle));
}

/*
Allows the item random selection to ensure all items are selected before an already selected item is selected again
*/
export function getRandomItemWithCache<T>(
  items: T[],
  cacheKey: string,
  keySelector: (item: T) => string,
  cachedRandomSelectionProvider: ICachedRandomSelectionProvider | null,
  updateCache: boolean
): T | undefined {
  return sample(items);
  // if we have an item in the list that is not available for some reason, we'd never update the cache with
  // the result and always return the invalid one, that gets us in a position where we can't select any other item
  // so lets just return a random from the array in all cases for now.
  // if (!updateCache) return sample(items);
  // let itemHistory = cachedRandomSelectionProvider
  //   ? cachedRandomSelectionProvider.getPreviousItemSelectionState()
  //   : {};
  // if (!items || items.length === 0) return undefined;
  // if (!itemHistory || !isRecord(itemHistory)) itemHistory = {};
  // let workingList: T[] = [];
  // if (
  //   itemHistory &&
  //   itemHistory[cacheKey] &&
  //   itemHistory[cacheKey].length > 0
  // ) {
  //   items.forEach((item) => {
  //     const idx = itemHistory[cacheKey].indexOf(keySelector(item));
  //     if (idx === -1) workingList.push(item);
  //   });
  // }
  // if (workingList.length === 0) {
  //   workingList = items; // pick from all when none left after filtering.
  //   if (
  //     cachedRandomSelectionProvider &&
  //     updateCache &&
  //     itemHistory &&
  //     itemHistory[cacheKey] &&
  //     itemHistory[cacheKey].length > 0
  //   ) {
  //     cachedRandomSelectionProvider.updatePreviousItemSelections(
  //       cacheKey,
  //       items.map((item) => keySelector(item)),
  //       false
  //     );
  //   }
  // }
  // const selectedItem = sample(workingList);
  // if (cachedRandomSelectionProvider && updateCache && selectedItem) {
  //   cachedRandomSelectionProvider.updatePreviousItemSelections(
  //     cacheKey,
  //     [keySelector(selectedItem)],
  //     true
  //   );
  // }
  // return selectedItem;
}

export function getRandomItem<T>(items: T[]): T | undefined {
  if (!items || items.length === 0) return undefined;
  return sample(items);
}

export const getRandom = (min: number, max: number): number => {
  return random(Math.ceil(min), Math.floor(max));
};

export const getDecimalRandom = (
  min: number,
  max: number,
  toNearest?: number | undefined
): number => {
  const result = random(min, max, true);
  if (!toNearest) return result;
  toNearest = 1 / toNearest;
  return Math.round(result * toNearest) / toNearest;
};

export const convertToCountTypeString = (
  count: number,
  countType: CountType
): string => {
  switch (countType) {
    case CountType.minutes: {
      const num = count;

      if (count < 60) {
        return Math.round(count) + " mins";
      }

      if (count < 1440) {
        const hours = num / 60;
        const rhours = Math.floor(hours);
        const hourMinutes = (hours - rhours) * 60;
        const rminutes = Math.round(hourMinutes);
        if (rhours === 0) return rminutes + " mins";
        else if (rminutes === 0) return rhours + " hrs";
        return rhours + " hrs " + rminutes + "mins";
      }

      const days = num / 60 / 24;
      const rdays = Math.floor(days);
      const dayHours = (days - rdays) * 24;
      const rhours = Math.floor(dayHours);
      if (rdays === 0) return rhours + " hrs";
      else if (rhours === 0) return rdays + " days";
      return rdays + " days " + rhours + "hrs";
    }
    default: {
      if (!countType || !CountType[countType]) return "";
      return `${count} ${CountType[countType].toString()}`;
    }
  }
};

export const convertFromMinutesToCountType = (
  minutes: number,
  countType: CountType,
  noRound?: boolean
): number => {
  const num = minutes;
  if (countType === CountType.hours) {
    const hours = num / 60;
    if (noRound) return hours;
    const rhours = Math.floor(hours);
    const hourMinutes = (hours - rhours) * 60;
    if (hourMinutes > 0) {
      return hours + 0.5;
    }
    return hours;
  }
  if (countType === CountType.days) {
    const days = num / 60 / 24;
    if (noRound) return days;
    const rdays = Math.floor(days);
    const dayHours = (days - rdays) * 24;
    if (dayHours > 0) return days + 0.5;
    return Math.round(days);
  }
  return num;
};

export const sortKeys = (arrayOfDates: Record<string, unknown>): string[] => {
  let arrayKeys = Object.keys(arrayOfDates).map((key: string) => parseInt(key));
  const maxKey = Math.max(...arrayKeys); // 31 31
  const minKey = Math.min(...arrayKeys); // 29 1
  const splitMonth = maxKey - minKey !== arrayKeys.length - 1; //31 -29 !== 2 // 31 -1 !== 2
  const tempKeys = [...arrayKeys];
  if (splitMonth) {
    arrayKeys = [];
    const currentDay = getDate(new Date());
    let dayCheck = currentDay;
    while (dayCheck > 0) {
      if (tempKeys.indexOf(dayCheck) > -1) {
        arrayKeys.push(dayCheck);
      }
      dayCheck -= 1;
    }

    // 31, 1 , 2 or 30, 31, 1
    let currentMax = maxKey;
    while (currentMax > currentDay) {
      if (tempKeys.indexOf(currentMax) > -1) {
        arrayKeys.push(currentMax);
      }
      currentMax -= 1;
    }
    return arrayKeys.reverse().map((key) => key.toString());
  }
  return arrayKeys.map((key) => key.toString());
};

export const updateArray = (
  array: ISettingsItem[],
  item: ISettingsItem
): void => {
  if (!array) array = [];
  const matchIdx = array.findIndex((i) => i.key === item.key);
  if (matchIdx === -1) {
    array.push(item);
    return;
  }
  array[matchIdx] = item;
};

const reISO =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
// eslint-disable-next-line no-useless-escape
const reMsAjax = /^\/Date\((d|-|.*)\)[\/|\\]$/;

const dateParser = function (key: string, value: string): Date | string {
  if (typeof value === "string") {
    let a = reISO.exec(value);
    if (a) return new Date(value);
    a = reMsAjax.exec(value);
    if (a) {
      const b = a[1].split(/[-+,.]/);
      return new Date(b[0] ? +b[0] : 0 - +b[1]);
    }
  }
  return value;
};

export const subscribe = (listeners: unknown[], listener: unknown): void => {
  if (listeners.indexOf(listener) > -1) return;
  listeners.push(listener);
};

export const unsubscribe = (listeners: unknown[], listener: unknown): void => {
  const idx = listeners.indexOf(listener);
  if (idx === -1) return;
  listeners.splice(idx, 1);
};


export const flipCoin = (): boolean => {
  return getRandom(0, 1) === 1;
};

export function ConcretizeItemObject(
  editItem: ISettingsItem
):
  | TaskItem
  | PenaltyItem
  | PermissionItem
  | EventItem
  | PositionItem {
  switch (editItem.itemType) {
    case ItemTypes.task:
      return new TaskItem(editItem as ITaskItem);
    case ItemTypes.event:
      return new EventItem(editItem as IEventItem);
    case ItemTypes.penalty:
      return new PenaltyItem(editItem as IPenaltyItem);
    case ItemTypes.permission:
      return new PermissionItem(editItem as IPermissionItem);
    case ItemTypes.position:
      return new PositionItem(editItem);
  }
  return editItem as
    | TaskItem
    | PenaltyItem
    | PermissionItem
    | EventItem
    | PositionItem;
}

export function getItemTypeExploreUrlName(key: ItemTypes): string {
  switch (key) {
    default:
    case ItemTypes.task: {
      return "tasks";
    }
    case ItemTypes.event: {
      return "events";
    }
    case ItemTypes.permission: {
      return "permissions";
    }
    case ItemTypes.penalty: {
      return "penalties";
    }
    case ItemTypes.position: {
      return "positions";
    }
  }
}

export function getItemTypeDisplayName(key: ItemTypes): string {
  switch (key) {
    default:
    case ItemTypes.task: {
      return "Task";
    }
    case ItemTypes.event: {
      return "Event";
    }
    case ItemTypes.permission: {
      return "Permission";
    }
    case ItemTypes.penalty: {
      return "Penalty";
    }
    case ItemTypes.position: {
      return "Position";
    }
  }
}

interface SettingProps {
  editorSettings?: Settings;
}

export const areSettingsPropsEqual = (
  prev: SettingProps,
  next: SettingProps
): boolean => {
  if (!prev.editorSettings && !next.editorSettings) return true;
  if (!prev.editorSettings && next.editorSettings) return false;
  if (prev.editorSettings && !next.editorSettings) return false;
  if (!prev.editorSettings?.isLoaded() && next.editorSettings?.isLoaded())
    return false;

  const result =
    (prev.editorSettings as Settings).getControl &&
    (next.editorSettings as Settings).getControl &&
    (prev.editorSettings as Settings).getControl()?.userid ===
    (next.editorSettings as Settings)?.getControl()?.userid;
  return result;
};

interface ControllerSettingProps {
  controller?: SessionController;
}
export const areControllerPropsEqual = (
  prev: ControllerSettingProps,
  next: ControllerSettingProps
): boolean => {
  if (!prev.controller && !next.controller) return true;
  if (!prev.controller && next.controller) return false;
  if (prev.controller && !next.controller) return false;
  if (
    !prev.controller?.getLoggedOnUserSettings().isLoaded() &&
    next.controller?.getLoggedOnUserSettings().isLoaded()
  )
    return false;
  return (
    prev.controller?.getLoggedOnUserSettings().getControl()?.userid ===
    next.controller?.getLoggedOnUserSettings().getControl()?.userid
  );
};

interface AdminSettingProps extends ControllerSettingProps, SettingProps { }

interface ClassNameSettingProps {
  className?: string;
}

export const areClassNamePropsEqual = (
  prev: ClassNameSettingProps,
  next: ClassNameSettingProps
): boolean => {
  return prev.className === next.className;
};

export const areAdminPropsEqual = (
  prev: AdminSettingProps,
  next: AdminSettingProps
): boolean => {
  return (
    areSettingsPropsEqual(prev, next) && areControllerPropsEqual(prev, next)
  );
};

export const alwaysPropsEqual = (): boolean => true;

export const isCategoryLimited = (
  cats: string[],
  limits: string[]
): string[] | undefined => {
  const matches: string[] = [];
  if (cats) {
    for (let ii = 0; ii < cats.length; ii += 1) {
      if (limits.indexOf(cats[ii]) !== -1) matches.push(cats[ii]);
    }
  }
  return matches.length > 0 ? matches : undefined;
};

export const createFilterRegularExpression = (filter: string) => {
  try {
    const regex = new RegExp(filter, "i");
    return regex;
  } catch (e) {
    return new RegExp("", "i");
  }
};
// decrypts the settings from server to make machine readable
export function decrypt<T>(encrypted: string, defaultVal: T): T {
  if (encrypted) {
    if (typeof encrypted !== "string") return encrypted;
    let str = encrypted;
    try {
      const buffer = Buffer.from(str, "base64");
      str = buffer.toString();
      if (str === "undefined") return defaultVal;
      return JSON.parse(str, dateParser);
    } catch (e) {
      return defaultVal;
    }
  }
  return defaultVal;
}
export function toTitleCase(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// encyrpts the settings to store on server db
export const encrypt = (shape: unknown): string => {
  const json = JSON.stringify(shape);
  const buffer = Buffer.from(json);
  return buffer.toString("base64");
};

export function filterListItems<T extends ISettingsItem>(
  items: T[],
  filterType: ItemEditorFilterTypes,
  settings: Settings,
  controller: SessionController
): T[] {
  if (!settings || filterType === ItemEditorFilterTypes.All) return items;
  const favourites = controller
    .getLoggedOnUserSettings()
    .getState<string[]>(STATE_KEY_FAVOURITE_ITEMS, []);
  return items.filter((i) => {
    if (!i.isNew && filterType === ItemEditorFilterTypes.AwaitingApproval)
      return false;
    if (
      !favourites.includes(i.key) &&
      filterType === ItemEditorFilterTypes.IsFavourite
    )
      return false;
    if (i.hidden && filterType === ItemEditorFilterTypes.ItemVisible)
      return false;
    if (!i.hidden && filterType === ItemEditorFilterTypes.ItemHidden)
      return false;
    if (!i.isDeleted && filterType === ItemEditorFilterTypes.ItemDeleted)
      return false;
    if (
      i.availabilityDayType !== AvailabilityDayType.AllDays &&
      filterType === ItemEditorFilterTypes.AllDayItems
    )
      return false;
    if (
      i.availabilityDayType !== AvailabilityDayType.BusyDays &&
      filterType === ItemEditorFilterTypes.BusyDayItems
    )
      return false;
    if (
      i.availabilityDayType !== AvailabilityDayType.FreeDays &&
      filterType === ItemEditorFilterTypes.FreeDayItems
    )
      return false;
    if (!i.isSystem && filterType === ItemEditorFilterTypes.InheritedFromSystem)
      return false;
    if (!i.isOwner && filterType === ItemEditorFilterTypes.InheritedFromOwner)
      return false;
    if (
      !i.isOverride &&
      filterType === ItemEditorFilterTypes.OverridedFromSystem
    )
      return false;
    if (
      filterType === ItemEditorFilterTypes.RecentlyAdded &&
      i.createdDate &&
      isBefore(i.createdDate, addMonths(new Date(), -1))
    )
      return false;
    if (!i.isCustom && filterType === ItemEditorFilterTypes.CustomItem)
      return false;
    if (i.itemType === ItemTypes.task) {
      const c = i as unknown as ITaskItem;
      if (c.votable !== true && filterType === ItemEditorFilterTypes.VoteItem)
        return false;
      if (
        filterType === ItemEditorFilterTypes.Unowned &&
        (c.isItem !== true ||
          c.hidden ||
          settings.checkItemIsOwned(c as IItemSupport))
      )
        return false;
      if (
        filterType === ItemEditorFilterTypes.Owned &&
        (c.isItem !== true ||
          c.hidden ||
          !settings.checkItemIsOwned(c as IItemSupport))
      )
        return false;
      if (
        filterType === ItemEditorFilterTypes.Clothes &&
        (!c.categories || !isClothesItem(c))
      )
        return false;
      if (
        filterType === ItemEditorFilterTypes.Outfits &&
        (!c.categories || !isOutfitItem(c))
      )
        return false;
      if (
        c.proofStatus !== ProofStatus.neverRequestProof &&
        filterType === ItemEditorFilterTypes.NoProofRequests
      )
        return false;
      if (
        c.captureLockImage !== true &&
        filterType === ItemEditorFilterTypes.CapturesLockImage
      )
        return false;
      if (
        !c.endTriggerEvent &&
        filterType === ItemEditorFilterTypes.HasEventTrigger
      )
        return false;
      if (
        (c.isItem || (c.itemsUsed && c.itemsUsed.length > 0)) &&
        filterType === ItemEditorFilterTypes.UsesNoItems
      )
        return false;
      if (!c.isItem && filterType === ItemEditorFilterTypes.IsToy) return false;
      if (
        (!c.options || c.options.length === 0) &&
        filterType === ItemEditorFilterTypes.OptionsDefined
      )
        return false;
      if (
        (!c.itemsUsed || c.itemsUsed.length === 0) &&
        filterType === ItemEditorFilterTypes.UsesItems
      )
        return false;
      if (
        c.preSelect !== PreselectType.permanent &&
        filterType === ItemEditorFilterTypes.PermanentlyApplied
      )
        return false;
      if (
        !c.suppressScheduleTask &&
        filterType === ItemEditorFilterTypes.SystemWontSchedule
      )
        return false;
      if (
        c.preSelect !== PreselectType.weekdays &&
        c.preSelect !== PreselectType.daily &&
        filterType === ItemEditorFilterTypes.WeekdayItem
      )
        return false;
      if (
        c.preSelect !== PreselectType.weekends &&
        c.preSelect !== PreselectType.daily &&
        filterType === ItemEditorFilterTypes.WeekendItem
      )
        return false;
      if (
        !c.useRouletteForOptions &&
        filterType === ItemEditorFilterTypes.RouletteUsed
      )
        return false;
      if (c.monitoredTimer && filterType === ItemEditorFilterTypes.NotMonitored)
        return false;
    }
    if (i.itemType === ItemTypes.penalty) {
      const c = i as unknown as IPenaltyItem;
      if (c.votable !== true && filterType === ItemEditorFilterTypes.VoteItem)
        return false;
      if (
        c.proofStatus !== ProofStatus.neverRequestProof &&
        filterType === ItemEditorFilterTypes.NoProofRequests
      )
        return false;
      if (
        c.captureLockImage !== true &&
        filterType === ItemEditorFilterTypes.CapturesLockImage
      )
        return false;
      if (
        !c.endTriggerEvent &&
        filterType === ItemEditorFilterTypes.HasEventTrigger
      )
        return false;
      if (
        (c.isItem || (c.itemsUsed && c.itemsUsed.length > 0)) &&
        filterType === ItemEditorFilterTypes.UsesNoItems
      )
        return false;
      if (!c.isItem && filterType === ItemEditorFilterTypes.IsToy) return false;
      if (
        (!c.options || c.options.length === 0) &&
        filterType === ItemEditorFilterTypes.OptionsDefined
      )
        return false;
      if (
        (!c.itemsUsed || c.itemsUsed.length === 0) &&
        filterType === ItemEditorFilterTypes.UsesItems
      )
        return false;
      if (
        !c.useRouletteForOptions &&
        filterType === ItemEditorFilterTypes.RouletteUsed
      )
        return false;
      if (c.monitoredTimer && filterType === ItemEditorFilterTypes.NotMonitored)
        return false;
    }
    if (i.itemType === ItemTypes.event) {
      const c = i as unknown as IEventItem;
      if (
        c.availability !== AvailabilityType.weekdays &&
        filterType === ItemEditorFilterTypes.WeekdayItem
      )
        return false;
      if (
        c.availability !== AvailabilityType.random &&
        filterType === ItemEditorFilterTypes.RandomDayItem
      )
        return false;
      if (
        c.availability !== AvailabilityType.weekends &&
        filterType === ItemEditorFilterTypes.WeekendItem
      )
        return false;
      if (
        c.availability !== AvailabilityType.specific &&
        filterType === ItemEditorFilterTypes.SpecificDayItem
      )
        return false;
    }
    if (i.itemType === ItemTypes.permission) {
      const c = i as unknown as IPermissionItem;
      if (
        (!c.options || c.options.length === 0) &&
        filterType === ItemEditorFilterTypes.OptionsDefined
      )
        return false;
      if (
        c.availability !== AvailabilityType.weekdays &&
        filterType === ItemEditorFilterTypes.WeekdayItem
      )
        return false;
      if (
        c.availability !== AvailabilityType.random &&
        filterType === ItemEditorFilterTypes.RandomDayItem
      )
        return false;
      if (
        c.availability !== AvailabilityType.weekends &&
        filterType === ItemEditorFilterTypes.WeekendItem
      )
        return false;
      if (
        c.availability !== AvailabilityType.specific &&
        filterType === ItemEditorFilterTypes.SpecificDayItem
      )
        return false;
      if (c.monitoredTimer && filterType === ItemEditorFilterTypes.NotMonitored)
        return false;
    }
    return true;
  });
}

export const getAvailabilityDaysLabel = (key: AvailabilityDayType): string => {
  const stringCode = AvailabilityDayType[key as unknown as AvailabilityDayType];
  switch (stringCode) {
    case "AllDays":
      return "Available all days";
    case "BusyDays":
      return "Only on busy days";
    case "FreeDays":
      return "Only on free days";
    default:
      return stringCode;
  }
};

export const shuffle = (arr: any[]): any[] => {
  return _shuffle(arr);
};

export const memberOwnerKeySuffix = "-ownerProfile";
export const getOwnerUserId = (userid: string) => {
  if (userid && userid.indexOf(memberOwnerKeySuffix) > -1) return userid;
  return `${userid}${memberOwnerKeySuffix}`;
};

export function filterData<T extends object>(
  items: T[],
  filter: string,
  keys: string[]
): T[] {
  if (!filter || keys.length === 0) return items;
  const parts = filter.split(" ");
  const partFunctions: ((item: T) => boolean)[] = [];
  parts.forEach((p) =>
    partFunctions.push((item: T): boolean => {
      const negate = p && p.startsWith("!");
      const results = keys.filter((key) => {
        if (!Object.keys(item).includes(key)) return negate ? true : false;
        let inspectVal = (item as any)[key];
        if (typeof inspectVal == "function") inspectVal = inspectVal();
        if (!inspectVal) return negate ? true : false;
        if (Array.isArray(inspectVal)) {
          return inspectVal.findIndex((part: string) => {
            return part.search(createFilterRegularExpression(p)) > -1;
          }) > -1
            ? !negate
            : negate;
        }
        if (negate) {
          return (
            inspectVal.search(createFilterRegularExpression(p.substring(1))) ===
            -1
          );
        }
        return inspectVal.search(createFilterRegularExpression(p)) > -1;
      });
      if (negate) return results.length === keys.length;
      return results.length > 0;
    })
  );
  return items.filter((item) => {
    const matches = partFunctions.filter((p) => p(item) === true);
    return matches.length === partFunctions.length;
  });
}

export function getFields<T>() {
  return new Proxy(
    {},
    {
      get: function (target, prop) {
        return prop;
      },
    }
  ) as {
      [P in keyof T]: P;
    };
}

export function roughSizeOfObject(object: any): number {
  const objectList = [];
  const stack = [object];
  let bytes = 0;

  while (stack.length) {
    const value = stack.pop();

    if (typeof value === "boolean") {
      bytes += 4;
    } else if (typeof value === "string") {
      bytes += value.length * 2;
    } else if (typeof value === "number") {
      bytes += 8;
    } else if (typeof value === "object" && objectList.indexOf(value) === -1) {
      objectList.push(value);

      for (const i in value) {
        stack.push(value[i]);
      }
    }
  }
  return bytes / 1024;
}

export const getDefaultVoteCount = (
  settingsItem: ICountSupport & IVotableItem
) => {
  if (!settingsItem) return 1;
  if (settingsItem.voteCount) return settingsItem.voteCount;

  let count = settingsItem.count;
  // CONVERT ALL VOTES TO MINUTES SO WE CAN DETERMINE THE COUNT PER VOTE
  if (count && settingsItem.countType === CountType.hours) {
    count = getCountMinutes(count, settingsItem.countType);
  } else if (count && settingsItem.countType === CountType.days) {
    count = getCountHours(count, settingsItem.countType);
  }
  const expectedCount = (settingsItem as ITaskTimerInfo | IPenaltyTimerInfo)
    .expectedCount;
  if (!count && expectedCount) {
    count = Math.floor(expectedCount * 0.33);
    if (count < 1) count = 1;
  } else if (!count) count = 1;
  else {
    count = Math.floor(count * 0.33);
    if (count < 1) count = 1;
  }

  return count;
};

export const getDefaultCountPerMistake = (
  settingsItem: ITaskItem | IPenaltyItem | ITaskTimerInfo | IPenaltyTimerInfo
) => {
  if (settingsItem.mistakeCount) return settingsItem.mistakeCount;

  let count = settingsItem.count;
  const expectedCount = (settingsItem as ITaskTimerInfo | IPenaltyTimerInfo)
    .expectedCount;
  if (!count && expectedCount) {
    count = Math.floor(expectedCount * 0.25);
    if (count < 1) count = 1;
  } else if (!count) count = 1;
  else {
    count = Math.floor(count * 0.25);
    if (count < 1) count = 1;
  }
  return count;
};

export const getVoteCountType = (countType: CountType) => {
  if (!isTimedItem(countType)) return countType;
  switch (countType) {
    case CountType.days:
      return CountType.hours;
    case CountType.hours:
      return CountType.minutes;
    default:
      return CountType.minutes;
  }
};

export const getPilloryTopicDefaults = (
  settingsItem: ITaskItem | IPenaltyItem | ITaskTimerInfo | IPenaltyTimerInfo
) => {
  const { voteCount, countType } = settingsItem;

  if (voteCount)
    return { count: voteCount, countType: getVoteCountType(countType) };

  return {
    count: getDefaultVoteCount(settingsItem),
    countType: getVoteCountType(countType),
  };
};

export const allUserOption: IUserRecord = {
  userid: "all",
  username: "All Owned Members",
} as IUserRecord;
export const dommesUserOption: IUserRecord = {
  userid: "dommes",
  username: "All Dommes",
} as IUserRecord;
export const subUserOption: IUserRecord = {
  userid: "subs",
  username: "All Subs",
} as IUserRecord;
export const houseUserOption: IUserRecord = {
  userid: "house",
  username: "House Slaves",
} as IUserRecord;

export const findUser = (
  username: string | undefined,
  toUserid: string | undefined,
  systemUser: IUserRecord | undefined,
  ownedSessions: IUserRecord[]
): IUserRecord | undefined => {
  if (!username) return undefined;
  if (username === allUserOption.username) return allUserOption;
  if (username === dommesUserOption.username) return dommesUserOption;
  if (username === subUserOption.username) return subUserOption;
  if (username === houseUserOption.username) return houseUserOption;

  if (username === systemUser?.username) {
    return { userid: systemUser?.userid as string, username };
  }

  if (username && toUserid) return { userid: toUserid, username }; //let people reply to those messages sent to them

  const user =
    ownedSessions && ownedSessions.find((user) => user.username === username);
  if (user) return user;
  return undefined;
};

export const getScheduleReason = (
  item: ISettingsItem,
  options?: {
    isPermanent?: boolean;
  }
): string => {
  if (item instanceof PositionItem) {
    return "Positions are enabled in the settings. This is a required Position which is set by the Count of Positions required daily in the Position Settings. When all positions are completed for the day, these positions will be removed from the to do list.";
  } else if (item instanceof PermissionItem) {
    return "This permission is set to be allowed for today. This means it matches the availability of the users diary, and is enabled and does not have a category in the list of limits of the member.";
  } else if (item instanceof EventItem) {
    return "This event is set to be allowed for today. This means it matches the availability of the users diary, and is enabled and does not have a category in the list of limits of the member.";
  } else if (item instanceof TaskItem) {
    if (options?.isPermanent) {
      return "This task is permanent. This means it will always apply and will never be stopped. This is set on the task in the settings of the task. The task does not have a category in the list of the limits of the member.";
    }
  }
  return "";
};

export function hasDiffs(
  oldObj: Record<string, any>,
  newObj: Record<string, any>
): boolean {
  return Object.keys(diffObjects(oldObj, newObj)).length > 0;
}

/**
 * Check if two arrays are equal
 * @param  {Array}   arr1 The first array
 * @param  {Array}   arr2 The second array
 * @return {Boolean}      If true, both arrays are equal
 */
const arraysMatch = function (arr1: any[], arr2: any[]) {
  // Check if the arrays are the same length
  if (arr1.length !== arr2.length) return false;

  // Check if all items exist and are in the same order
  for (let i = 0; i < arr1.length; i++) {
    if (compare(arr1[i], arr2[i])) return false;
  }

  // Otherwise, return true
  return true;
};

/**
   * Compare two items and push non-matches to object
   * @param  {*}      item1 The first item
   * @param  {*}      item2 The second item
   * @param  {String} key   The key in our object
   */
const compare = function (
  item1: Record<string, any>,
  item2: Record<string, any>
) {
  // Get the object type
  const type1 = Object.prototype.toString.call(item1);
  const type2 = Object.prototype.toString.call(item2);

  // If type2 is undefined it has been removed
  if (type1 === "[object Undefined]" && type2 === "[object Undefined]") {
    // this is ok as it was never there
    return;
  }
  // If type2 is undefined it has been removed
  if (type2 === "[object Undefined]") {
    return null;
  }

  // If items are different types
  if (type1 !== type2) {
    return item2;
  }

  // If an object, compare recursively
  if (type1 === "[object Object]") {
    const objDiff = diffObjects(item1, item2);
    if (Object.keys(objDiff).length > 0) {
      return objDiff;
    }
    return;
  }

  // If an array, compare
  if (type1 === "[object Array]") {
    if (!arraysMatch(item1 as Array<any>, item2 as Array<any>)) {
      return item2;
    }
    return;
  }

  // Else if it's a function, convert to a string and compare
  // Otherwise, just compare
  if (type1 === "[object Function]") {
    if (item1.toString() !== item2.toString()) {
      return item2;
    }
  } else {
    if (item1 !== item2) {
      return item2;
    }
  }
};

export function diffObjects(
  oldObj: Record<string, any>,
  newObj: Record<string, any>
): Record<string, any> {
  if (!oldObj && newObj) return newObj;
  if (oldObj && !newObj) return oldObj;
  // Make sure an object to compare is provided
  if (!newObj || Object.prototype.toString.call(newObj) !== "[object Object]") {
    return oldObj;
  }
  const diffs: Record<string, any | null> = {};
  let key;




  // Loop through the first object
  for (key in oldObj) {
    // eslint-disable-next-line no-prototype-builtins
    if (oldObj.hasOwnProperty(key)) {
      const result = compare(oldObj[key as string], newObj[key]);
      // Don't skip falsy values like 0, false, or empty string - they are valid changes
      // Only skip undefined (no change detected)
      if (result !== undefined) diffs[key] = result;
    }
  }

  // Loop through the second object and find missing items
  for (key in newObj) {
    // eslint-disable-next-line no-prototype-builtins
    if (newObj.hasOwnProperty(key)) {
      if (!oldObj[key] && oldObj[key] !== newObj[key]) {
        const newVal = newObj[key];
        if (Array.isArray(newVal) && (newVal as Array<any>).length === 0)
          continue;
        // Don't skip falsy values like 0, false, or empty string as they are valid values
        // Only skip null, undefined, or empty objects
        if (newVal == null) continue;
        if (typeof newVal === 'object' && !Array.isArray(newVal) && Object.keys(newVal).length === 0) continue;
        diffs[key] = newObj[key];
      }
    }
  }

  // Return the object of differences
  return diffs;
}

export const isClothesCategory = (c: string) =>
  c.toLowerCase().includes("clothes") ||
  c.toLowerCase().includes("lingerie") ||
  c.toLowerCase().includes("hosiery");
const isFootwearCategory = (c: string) =>
  c.toLowerCase().includes("shoes") || c.toLowerCase().includes("footwear");
const isUnderwearCategory = (c: string) =>
  c.toLowerCase().includes("lingerie") || c.toLowerCase().includes("hosiery");
export const isOutfitCategory = (c: string) =>
  c.toLowerCase().includes("outfit");
export const isChastityCategory = (c: string) =>
  c.toLowerCase().includes("chastity");
const isAccessoryCategory = (c: string) =>
  c.toLowerCase().includes("accessor")
  || c.toLowerCase().includes("jewellery")
  || c.toLowerCase().includes("jewelry")
  || c.toLowerCase().includes("makeup")
  || c.toLowerCase().includes("baby")
  || c.toLowerCase().includes("abdl")
  || c.toLowerCase().includes("bondage");

export const isChastityItem = (item: ICategoryItem) =>
  (item &&
    item.categories &&
    item.categories.find((c) => isChastityCategory(c)) !== undefined) ?? false;

export const isAccessoryItem = (item: ICategoryItem) =>
  (item &&
    item.categories &&
    item.categories.find((c) => isAccessoryCategory(c)) !== undefined) ?? false;
export const isFootwearItem = (item: ICategoryItem) =>
  (item &&
    item.categories &&
    item.categories.find((c) => isFootwearCategory(c)) !== undefined) ?? false;
export const isUnderwearItem = (item: ICategoryItem) =>
  (item &&
    item.categories &&
    item.categories.find((c) => isUnderwearCategory(c)) !== undefined) ?? false;
export const isOutfitItem = (item: ICategoryItem) =>
  (item &&
    item.categories &&
    item.categories.find((c) => isOutfitCategory(c)) !== undefined) ?? false;
export const isClothesItem = (item: ICategoryItem) =>
  (item &&
    item.categories &&
    item.categories.find((c) => isClothesCategory(c)) !== undefined) ?? false;

export const isOutfitEventItem = (item: IEventItem, tasks: ISettingsItem[]) => {
  if (!item.taskKeys) return [];
  return (item.taskKeys as string[])
    .map((t) => tasks.find((a) => a.key === t) as ITaskItem)
    .filter((t) => t && (isClothesItem(t) || isOutfitItem(t)));
};

export const getDefaultQuickFilter = (subType: SettingsSubType | undefined) => {
  if (subType === undefined) return ItemEditorFilterTypes.All;
  switch (subType) {
    case SettingsSubType.New:
      return ItemEditorFilterTypes.AwaitingApproval;
    case SettingsSubType.Favourite:
      return ItemEditorFilterTypes.IsFavourite;
    case SettingsSubType.Hidden:
      return ItemEditorFilterTypes.ItemHidden;
    case SettingsSubType.Unowned:
      return ItemEditorFilterTypes.Unowned;
    case SettingsSubType.Owned:
      return ItemEditorFilterTypes.Owned;
    case SettingsSubType.Clothes:
      return ItemEditorFilterTypes.Clothes;
    case SettingsSubType.Outfits:
      return ItemEditorFilterTypes.Outfits;
    default:
      return ItemEditorFilterTypes.All;
  }
};

export const valueMatches = (userVal: any, baseVal: any): boolean => {
  if (userVal === baseVal) return true;
  if (
    userVal &&
    baseVal &&
    baseVal instanceof Array &&
    userVal instanceof Array &&
    baseVal.length === userVal.length
  ) {
    for (let idx = 0; idx < userVal.length; idx += 1) {
      const i = userVal[idx];
      const b = baseVal[idx];
      const matches = valueMatches(i, b);
      if (!matches) return false;
    }
    return true;
  }
  return false;
};

export const hasDifference = (diff?: IDiffCompare): boolean => {
  return (
    diff !== undefined &&
    diff.differences !== undefined &&
    diff.field !== undefined &&
    diff.differences.indexOf(diff.field) > -1
  );
};

const getDiffCompareVal = (diff?: IDiffCompare): any => {
  return (
    diff !== undefined &&
    diff.differences !== undefined &&
    diff.field !== undefined &&
    diff.compareItem &&
    (diff.compareItem as any)[diff.field as string]
  );
};

export const getPrevDiffValLabel = (diff?: IDiffCompare) => {
  const diffVal = getDiffCompareVal(diff);
  if (!diffVal) return "(new)";
  return `(was: ${diffVal})`;
};

export interface IDiffCompare {
  field: string | undefined;
  differences?: string[];
  compareItem?: any;
  currentVals?: any[];
  showDeletes?: boolean;
  getDisplayNameForKey?: (key: any) => string | undefined;
}

// const lazyRetry = function(componentImport) {

//     return new Promise((resolve, reject) => {

//   const hasRefreshed = JSON.parse(
//       window.sessionStorage.getItem('retry-lazy-refreshed') || 'false'
//   );

//   componentImport().then((component) => {
//       window.sessionStorage.setItem('retry-lazy-refreshed', 'false');
//       resolve(component);
//   }).catch((error) => {
//       if (!hasRefreshed) { // not been refreshed yet
//           window.sessionStorage.setItem('retry-lazy-refreshed', 'true');
//           return window.location.reload(); // refresh the page
//       }
//       reject(error);
//   });
//  });
// };
// import pages as

//     const YourPage = React.lazyWithRetry(()=> lazyRetry(() => import('./pages/YourPage')))
